import {
  Card,
  Divider,
  Drawer,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Zoom
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  MoreVertRounded,
  VisibilityOffRounded,
  VisibilityRounded
} from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link as RRLink, NavLink } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { ORDER } from '../../../common/enum/order';
import { PaginationProps } from '../../../common/interface/pagination';
import EnhancedTableToolbar from '../../../components/EnhancedTableToolbar';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import {
  Contractor,
  getContractorsAsync,
  getContractorsMasterDataAsync,
  showHideContractorAsync
} from './contractorSlice';

interface ContractorParamProps extends PaginationProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      borderRadius: '16px',
      display: 'flex',
      padding: theme.spacing(1, 1, 1, 2),
      marginBottom: theme.spacing(1.5),
      '&:last-child': {
        marginBottom: 0
      }
    },
    cardContainer: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      }
    },
    contractorName: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1.5)
    },
    fab: {
      paddingRight: theme.spacing(3),
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 100
    },
    marginRight1: {
      marginRight: theme.spacing(1)
    }
  })
);

export default function ContractorView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();

  const sortOptions = [
    { value: 'id', text: t('contractorView.sortOptions.createdDate') },
    { value: 'name', text: t('contractorView.sortOptions.name') }
  ];
  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const { masterData: contractors, count } = useSelector(
    (state: RootState) => state.contractors
  );

  const [openOption, setOpenOption] = useState<boolean>(false);
  const [selectedContractor, setSelectedContractor] =
    useState<Contractor | null>(null);
  const [contractorParams, setContractorParams] =
    useState<ContractorParamProps>({
      page: 0,
      limit: 30,
      sortBy: 'name',
      orderBy: ORDER.asc
    });

  useEffect(() => {
    loadingContractor(contractorParams);
  }, []);

  function loadingContractor(params: ContractorParamProps) {
    dispatch(getContractorsMasterDataAsync(params));
    setContractorParams(params);
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) {
    loadingContractor({
      ...contractorParams,
      page: newPage - 1 //The first value of pagination is 1, not 0
    });
  }

  function toggleDrawer(open: boolean, contractor?: Contractor) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && contractor) {
        setSelectedContractor(contractor);
      }
      setOpenOption(open);
    };
  }

  async function handleShowHideContractor(id: number, isActive: boolean) {
    await dispatch(
      showHideContractorAsync({ id, isActive: !isActive })
    ).unwrap();
    dispatch(getContractorsAsync());
    loadingContractor(contractorParams);
  }

  return (
    <div className={classes.root}>
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText primary={selectedContractor?.name} />
            </ListItem>
          </List>
          <Divider />
          <List>
            <ListItem
              component={NavLink}
              to={`contractors/${selectedContractor?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('contractorView.drawer.editContractor')} />
            </ListItem>
            <ListItem
              button
              onClick={() => {
                if (selectedContractor)
                  handleShowHideContractor(
                    selectedContractor.id,
                    selectedContractor.isActive
                  );
              }}
            >
              <ListItemIcon>
                {selectedContractor?.isActive ? (
                  <VisibilityOffRounded />
                ) : (
                  <VisibilityRounded />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  selectedContractor?.isActive
                    ? t('contractorView.drawer.hideContractor')
                    : t('contractorView.drawer.showContractor')
                }
              />
            </ListItem>
            <ListItem button onClick={() => {}} disabled>
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('contractorView.drawer.deleteContractor')}
                secondary={t('contractorView.drawer.deleteFunctionNotAvailable')}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      <Breadcrumb
        name="masterDataName"
        data={{
          masterDataName: t('contractorView.breadcrumb.masterDataName'),
          masterDataRef: 'contractors'
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('contractorView.title')}
      </Typography>
      <EnhancedTableToolbar
        name={t('contractorView.toolbar.name')}
        params={contractorParams}
        sortOptions={sortOptions}
        loadingElement={loadingContractor}
      />
      {contractors.map((contractor) => {
        return (
          <Card className={classes.card} key={contractor.id}>
            <div className={classes.cardContainer}>
              <div className={classes.contractorName}>
                {!contractor.isActive && (
                  <Tooltip title={t('contractorView.drawer.hideContractor') as string} arrow>
                    <VisibilityOffRounded
                      className={classes.marginRight1}
                      fontSize="small"
                      color="disabled"
                    />
                  </Tooltip>
                )}
                <Typography
                  color={contractor.isActive ? 'textPrimary' : 'textSecondary'}
                >
                  {contractor.name}
                </Typography>
              </div>
            </div>
            <div>
              <IconButton size="small" onClick={toggleDrawer(true, contractor)}>
                <MoreVertRounded />
              </IconButton>
            </div>
          </Card>
        );
      })}
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / contractorParams.limit)}
          shape="rounded"
          page={contractorParams.page + 1}
          onChange={handleChangePage}
        />
      </div>
      <Zoom
        in
        timeout={transitionDuration}
        style={{
          transitionDelay: `${transitionDuration.exit}ms`
        }}
        unmountOnExit
      >
        <Fab
          className={classes.fab}
          component={RRLink}
          to={`contractors/create`}
          variant="extended"
          color="primary"
          aria-label={t('contractorView.fab.ariaLabel') as string}
        >
          <AddRounded className={classes.marginRight1} />
          {t('contractorView.fab.newContractor')}
        </Fab>
      </Zoom>
    </div>
  );
}
